import { UserRole } from '@app/features/users/user.model';

/** L'attribut qui marque un élément d'écran qu'un parcours peut mettre en surbrillance. */
export const TOUR_ANCHOR_ATTRIBUTE = 'data-tour-anchor';

/**
 * Un geste que le parcours fait à la place de l'utilisateur, sur l'élément de l'étape ou sur un
 * élément qu'il contient. Un geste peut viser une autre ancre, que le geste précédent fait
 * apparaître. En démonstration, rien n'est enregistré : le parcours peut agir.
 */
export type TourAction = ({ kind: 'click' } | { kind: 'type'; value: string | (() => string) }) & {
  /** L'ancre que vise le geste, quand ce n'est pas celle de l'étape. */
  anchor?: string;
  selector?: string;
};

/** Un appel à l'API, tel que les données fictives le reçoivent : `POST /contracts/<id>/amendments`. */
export type TourCall = `${'GET' | 'POST' | 'PUT' | 'DELETE'} /${string}`;

/**
 * Une étape d'un parcours : l'élément à mettre en surbrillance, ce que la zone de texte en dit, et
 * les gestes qui mènent à l'étape suivante. La CI rejoue ces mêmes étapes (`tour-replay.spec.ts`).
 */
export interface TourStep {
  /** La valeur de `data-tour-anchor` que porte l'élément ciblé. */
  anchor: string;
  title: string;
  text: string;
  /** Ce qu'il faut faire pour que l'élément apparaisse, tant qu'il n'est pas à l'écran. */
  whenAbsent: string;
  /**
   * Ce que « Suivant » fait, dans l'ordre : un geste, ou une suite de gestes qui n'ont de sens
   * qu'ensemble, comme l'heure de début puis l'heure de fin d'un créneau (décision 2026-032/17).
   */
  actions?: TourAction[];
  /**
   * L'appel que les gestes de l'étape doivent atteindre : celui du cas d'usage, une écriture — ou
   * une lecture, quand le cas d'usage ne fait que lire. Le rejeu exige de le voir passer ; les
   * étapes qui suivent, comme l'historique, se jouent après lui. Chaque variante du parcours en
   * porte un seul, sur une étape réservée à ses rôles quand il en dépend (décision 2026-032/21).
   */
  reaches?: TourCall;
  /** Sans rôles, l'étape vaut pour tous ; sinon, elle n'est montrée qu'à ces rôles. */
  roles?: UserRole[];
}

/** Un parcours de découverte : un cas d'usage d'un domaine, joué sur des données fictives. */
export interface Tour {
  /**
   * `<domaine>/<cas-d-usage>`, l'adresse de l'onglet de démonstration. Stable : une nouveauté y
   * renvoie, et un identifiant publié ne disparaît pas.
   */
  id: string;
  domain: string;
  title: string;
  /**
   * Les règles de gestion que le parcours illustre, citées dans son instantané : un cadrage qui en
   * modifie une y trouve les parcours à relire (RG-parcours-instantane).
   */
  rules: string[];
  /** L'écran où commence le parcours. */
  startUrl: string;
  steps: TourStep[];
}

/**
 * Une suite d'étapes que voient certains rôles, et qui ne se rejoue qu'une fois : sous un
 * utilisateur fictif qui a ces rôles, jusqu'à l'appel final qui lui est propre (décision 2026-032/22).
 */
export interface TourVariant {
  roles: UserRole[];
  steps: TourStep[];
  call: TourCall | undefined;
}
